import { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { $generateHtmlFromNodes } from '@lexical/html';
import { LinkNode } from '@lexical/link';
import { $getRoot, $getSelection, $isRangeSelection, type EditorState, type LexicalEditor } from 'lexical';
import { ComposerToolbar } from './ComposerToolbar';
import { Skeleton } from '../../../../components/ui/skeleton';
import { SubmitShortcutPlugin } from './SubmitShortcutPlugin';
import { stripHtmlAttributes } from './stripHtmlAttributes';
import { $seedFromHtml, seedFrom } from './headless';
import { COMPOSER_NAMESPACE, COMPOSER_NODES, composerTheme } from './config';

const rethrow = (e: Error): never => {
  throw e;
};

// A saved draft's formatting, read back into the editor's own nodes; when it yields no words, the text.
function seedFromHtml(html: string, text: string) {
  return (editor: LexicalEditor) => {
    $seedFromHtml(editor, html);
    if ($getRoot().getTextContent().trim() === '' && text) {
      $getRoot().clear();
      seedFrom(text)();
    }
  };
}

// Where a link is not allowed, a link that arrives anyway (a seed, a paste) keeps its words and loses the link.
function NoLinksPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(
    () =>
      editor.registerNodeTransform(LinkNode, (link) => {
        link.getChildren().forEach((child) => link.insertBefore(child));
        link.remove();
      }),
    [editor],
  );
  return null;
}

// Follows `editable` after mount; Lexical reads its initial value only once.
function EditableSync({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => editor.setEditable(editable), [editor, editable]);
  return null;
}

export interface ComposerEditorHandle {
  /** Inserts at the current cursor (falls back to the document end when the
   *  editor isn't focused) — MergeTagChips' "append a tag" affordance,
   *  without losing whatever formatting/cursor position the human has. */
  insertTextAtCursor: (text: string) => void;
  /** Wholesale replace with plain text. Goes through the editor's history, so the editor's own undo brings back what was there. */
  replaceWithPlainText: (text: string) => void;
  /** Wholesale replace — a chosen suggestion, counter or Help me write draft — keeping its formatting when it has
   *  some; HTML that yields no words falls back to the text. Undoable like replaceWithPlainText.
   *  False when there is no editor to write into, so the caller doesn't record a suggestion that never landed. */
  replaceWithContent: (text: string, html?: string) => boolean;
  /** Puts the caret in the message box. */
  focus: () => void;
}

/** Bridges the live LexicalEditor instance out to ComposerEditor's
 *  imperative handle — LexicalComposer only exposes it via context. */
function EditorHandleBridge({ editorRef }: { editorRef: React.MutableRefObject<LexicalEditor | null> }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef.current = editor;
    return () => {
      editorRef.current = null;
    };
  }, [editor, editorRef]);
  return null;
}

export interface ComposerEditorProps {
  /** Seed text, read once on mount (e.g. QuoteBlock's Confirm/Counter draft).
   *  Remount via `key` to re-seed after the identity changes. */
  initialText?: string;
  /** Seed formatting, read once on mount; wins over initialText when set. */
  initialHtml?: string;
  placeholder: string;
  /** Fires on every edit with the plain-text extraction (for `body`, merge
   *  tags, canSend gating) and the rich HTML rendering (for `bodyHtml`) —
   *  both derived from the same editor state; server-side sanitizes again
   *  before storage/send. */
  onChange: (plainText: string, html: string) => void;
  onSubmitShortcut: () => void;
  handleRef?: React.Ref<ComposerEditorHandle>;
  /** Rendered under the message box, inside the editor context. */
  belowEditor?: React.ReactNode;
  /** Text is being written for the box: a text-shaped skeleton stands in for it (the editor stays mounted). */
  writingLabel?: string;
  /** Places the formatting toolbar (it needs the editor context); defaults to the toolbar alone under the box. */
  renderFooter?: (toolbar: React.ReactNode) => React.ReactNode;
  /** Places the toolbar above the message box instead (it needs the editor context); wins over renderFooter. */
  renderHeader?: (toolbar: React.ReactNode) => React.ReactNode;
  /** Adds a numbered-list button next to the bulleted one. */
  numberedList?: boolean;
  /** False drops the Link button: a first touch never carries a link. */
  allowLink?: boolean;
  /** False leaves focus where it is on mount (another field takes it). */
  autoFocus?: boolean;
  /** The message box's accessible name. */
  ariaLabel?: string;
  /** Id of the text that describes the message box (e.g. why there is no Link). */
  ariaDescribedBy?: string;
  /** Marks the message box invalid (a field error is shown for it). */
  ariaInvalid?: boolean;
  /** False makes the box and its toolbar read-only (e.g. while a save is in flight). */
  editable?: boolean;
  /** False drops the always-mounted "writing" status region, for an editor nothing writes into. */
  liveRegion?: boolean;
}

/** Composer's own minimal Lexical setup — Bold/Italic/Underline/link/list
 *  only (Figma's toolbar), distinct from scriptDoc's fuller editor. */
export function ComposerEditor({ initialText, initialHtml, placeholder, onChange, onSubmitShortcut, handleRef, belowEditor, renderFooter, renderHeader, numberedList = false, writingLabel, allowLink = true, autoFocus = true, ariaLabel = 'Message', ariaDescribedBy, ariaInvalid = false, editable = true, liveRegion = true }: ComposerEditorProps) {
  const editorRef = useRef<LexicalEditor | null>(null);

  const initialConfig = useMemo(
    () => ({
      namespace: COMPOSER_NAMESPACE,
      nodes: [...COMPOSER_NODES],
      theme: composerTheme,
      onError: rethrow,
      editorState: initialHtml ? seedFromHtml(initialHtml, initialText ?? '') : initialText ? seedFrom(initialText) : undefined,
    }),
    // intentionally empty: the seed is read once per mount (see initialText's
    // JSDoc above) — including it would re-seed on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useImperativeHandle(
    handleRef,
    () => ({
      insertTextAtCursor: (text) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.update(() => {
          const sel = $getSelection();
          if ($isRangeSelection(sel)) {
            sel.insertText(text);
          } else {
            $getRoot().selectEnd().insertText(text);
          }
        });
      },
      replaceWithPlainText: (text) => {
        const editor = editorRef.current;
        if (!editor) return;
        editor.update(() => {
          $getRoot().clear();
          seedFrom(text)();
        });
      },
      replaceWithContent: (text, html) => {
        const editor = editorRef.current;
        if (!editor) return false;
        editor.update(() => {
          $getRoot().clear();
          if (html) seedFromHtml(html, text)(editor);
          if ($getRoot().isEmpty()) seedFrom(text)();
        });
        return true;
      },
      focus: () => editorRef.current?.focus(),
    }),
    [],
  );

  const handleChange = (state: EditorState, editor: LexicalEditor) => {
    state.read(() => {
      const text = $getRoot().getTextContent();
      const html = text.trim() === '' ? '' : stripHtmlAttributes($generateHtmlFromNodes(editor, null));
      onChange(text, html);
    });
  };

  const toolbar = <ComposerToolbar disabled={!!writingLabel || !editable} allowLink={allowLink} numberedList={numberedList} />;

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <EditorHandleBridge editorRef={editorRef} />
      {/* Always mounted, so the message is announced when it appears rather than with a fresh region. */}
      {liveRegion ? (
        <p role="status" className="sr-only">
          {writingLabel ?? ''}
        </p>
      ) : null}
      {writingLabel && (
        <div data-testid="composer-writing" aria-hidden className="flex flex-col gap-2 p-4">
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-4/5" />
          <Skeleton className="h-4 w-2/5" />
        </div>
      )}
      {renderHeader ? renderHeader(toolbar) : null}
      <div className="relative" hidden={!!writingLabel}>
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              aria-label={ariaLabel}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid || undefined}
              // Figma's Editor body text is 16px/24px (fontSize 16, lineHeight
              // 24) — text-sm/relaxed (14px) read noticeably smaller.
              // Capped so a long draft scrolls inside the editor instead of squeezing the thread above it.
              className="max-h-[40dvh] min-h-[57px] resize-none overflow-y-auto p-4 text-base/6 text-foreground outline-none"
            />
          }
          placeholder={
            <p aria-hidden className="pointer-events-none absolute left-4 top-4 text-base/6 text-(--foreground-subtle)">
              {placeholder}
            </p>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
      </div>
      {belowEditor}
      {renderHeader ? null : renderFooter ? renderFooter(toolbar) : toolbar}
      <HistoryPlugin />
      <ListPlugin />
      <LinkPlugin />
      {allowLink ? null : <NoLinksPlugin />}
      <EditableSync editable={editable} />
      {autoFocus ? <AutoFocusPlugin /> : null}
      <SubmitShortcutPlugin onSubmit={onSubmitShortcut} />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
    </LexicalComposer>
  );
}
